import { API_ENDPOINT, AI_ENDPOINT, WEBHOOK_ENDPOINT } from '@Env/variables';
import {
  Assigned,
  AssignedRecordResponse,
  promptAnalyzer,
  TicketInput,
  TicketClassificationResponse,
  AnalyzerWebhookReport,
  SelectedTicketEntry,
  AnalyzerPodReport,
  AnalyzerMappings,
  AnalyzerWeeklyPrompt,
} from '@Models/types';
import { createAxiosInstance } from './axios';

export const analyzerReportsAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, WEBHOOK_ENDPOINT);

  const analyzer = {
    getMappings: async (): Promise<AnalyzerMappings[]> => {
      const { data } = await axios.get('/api/analyzer/getMappings');
      return data;
    },
    updateMappings: async (mappings: AnalyzerMappings) => {
      const { data } = await axios.post('/api/analyzer/updateMappings', {
        ...mappings,
      });
      return data;
    },
    deleteMapping: async (username: string) => {
      const { data } = await axios.post('/api/analyzer/deleteMapping', {
        username,
      });
      return data;
    },

    getPrompts: async (): Promise<AnalyzerWeeklyPrompt[]> => {
      const { data } = await axios.get('/api/analyzer/prompts');
      return data;
    },

    createPrompt: async (
      prompt: Omit<
        AnalyzerWeeklyPrompt,
        '_id' | 'authorEmail' | 'dateCreated' | 'isDefault'
      >
    ) => {
      const { data } = await axios.post('/api/analyzer/prompts', prompt);
      return data;
    },

    updatePrompt: async (
      originalTitle: string,
      originalAuthor: string,
      prompt: Partial<AnalyzerWeeklyPrompt>
    ) => {
      const { data } = await axios.post('/api/analyzer/prompts/update', {
        originalTitle,
        originalAuthor,
        ...prompt,
      });
      return data;
    },

    deletePrompt: async (promptTitle: string, authorEmail: string) => {
      const { data } = await axios.post('/api/analyzer/prompts/delete', {
        promptTitle,
        authorEmail,
      });
      return data;
    },

    setDefaultPrompt: async (promptTitle: string, authorEmail: string) => {
      const { data } = await axios.post('/api/analyzer/prompts/setDefault', {
        promptTitle,
        authorEmail,
      });
      return data;
    },
  };

  return analyzer;
};

export const analyzerAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, API_ENDPOINT);

  const analyzer = {
    getAnalyzerReports: async (): Promise<AnalyzerWebhookReport[]> => {
      const { data } = await axios.get('/api/getAnalyzerReports');
      return data;
    },

    getAnalyzerWeeklyReports: async (
      podId: string
    ): Promise<AnalyzerPodReport[]> => {
      const { data } = await axios.get('/api/getAnalyzerWeeklyReports', {
        params: { podId },
      });
      return data;
    },

    tagAnalyzerReports: async (
      selectedTickets: Record<string, SelectedTicketEntry>
    ) => {
      const { data } = await axios.post('/api/tagAnalyzerReports', {
        selectedTickets,
      });
      return data;
    },

    getAnalyzerRecords: async (
      reference_prefixes: string[],
      startPage: number = 1,
      endPage: number = 2
    ): Promise<Assigned[]> => {
      const { data } = await axios.post('/api/getAnalyzerRecords', {
        reference_prefixes,
        startPage,
        endPage,
      });

      const assigned = data.assigned.map((record: AssignedRecordResponse) => ({
        reference_num: record.reference_num,
        name: record.name,
        status: record.status,
        record_type: record.record_type,
        id: record.id,
        workflow_status: record.workflow_status
          ? {
              id: record.workflow_status.id,
              name: record.workflow_status.name,
              position: record.workflow_status.position,
              complete: record.workflow_status.complete,
              color: record.workflow_status.color,
            }
          : undefined,
        due_date: record.due_date || 'No due date set',
        current_task_user: record.current_task_user,
        description: record.description || 'No description available',
        priority: record.priority || 'No priority set',
        url: record.url,
        created_at: record.created_at,
        comments: record.comments,
        tags: record.tags,
      }));
      return assigned;
    },

    tagTickets: async (tickets: Assigned[]): Promise<Assigned[]> => {
      const { data } = await axios.post('/api/taggedTickets', {
        tickets,
      });
      return data.tagged;
    },

    getAnalyzerPrompts: async (): Promise<promptAnalyzer[]> => {
      const { data } = await axios.post('/api/analyzerPrompt', {
        method: 'GET',
      });
      return data.prompts;
    },

    addAnalyzerPrompt: async (
      prompt: promptAnalyzer
    ): Promise<promptAnalyzer> => {
      const { data } = await axios.post('/api/addAnalyzerPrompt', {
        ...prompt,
      });
      return data.prompt;
    },

    updateAnalyzerPrompt: async (
      prompt: promptAnalyzer
    ): Promise<promptAnalyzer> => {
      const { data } = await axios.post('/api/updateAnalyzerPrompt', {
        ...prompt,
      });
      return data.prompt;
    },

    deleteAnalyzerPrompt: async (promptId: string): Promise<void> => {
      await axios.post('/api/deleteAnalyzerPrompt', {
        id: promptId,
      });
    },
    //internal call
    editAnalyzerPrompt: async (
      prompt: promptAnalyzer
    ): Promise<promptAnalyzer[]> => {
      await analyzer.updateAnalyzerPrompt(prompt);
      const refreshedPrompts = await analyzer.getAnalyzerPrompts();
      return refreshedPrompts;
    },
    evaluateReport: async (
      week: string,
      podId: string,
      evaluated: boolean
    ): Promise<void> => {
      await axios.post('/api/evaluateReport', {
        week,
        podId,
        evaluated,
      });
    },
  };
  return analyzer;
};

export const analyzerAiAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, AI_ENDPOINT);

  return {
    classifyTickets: async (
      tickets: TicketInput[],
      selectedPrompt?: promptAnalyzer | null
    ): Promise<TicketClassificationResponse> => {
      const { data } = await axios.post('/api/classifyTickets', {
        tickets,
        selectedPrompt,
      });
      return data;
    },
  };
};
